import { Injectable } from '@angular/core';

import {
  admit,
  BatchEmission,
  BatchEntry,
  BatchState,
  EMPTY_BATCH_STATE,
  flush,
  isClaimed,
  recordClaim,
} from './desktop-notification-batch';

/** Transient — deliberately absent from PRESERVED_STORAGE_KEYS, so logout clears it. */
export const DESKTOP_NOTIFICATION_BATCH_KEY = 'wolsal.ui.notifications.batch';

/** Name of the cross-tab lock serialising the ledger's read-check-write. */
export const DESKTOP_CLAIM_LOCK = 'volta.notifications.claim';

/**
 * Holds the batching window in localStorage so every tab reduces the same state.
 * Per-tab state would let a tab opened mid-burst emit "1 order is ready" and replace
 * the other tabs' correct count under the shared notification tag.
 */
@Injectable({ providedIn: 'root' })
export class DesktopNotificationBatchService {
  /** Recorded on each claim, so the ledger says which tab took an event. */
  private readonly tabId = Math.random().toString(36).slice(2, 10);

  /**
   * True when this tab is the one that should deliver the event. Every tab holds its own
   * SSE stream and sees the same event; the shared ledger is what elects a single deliverer.
   * An unreadable store grants the claim — a duplicate banner is a smaller failure than silence.
   *
   * `localStorage` has no compare-and-swap, so two tabs can read the same unclaimed state
   * and both write a claim. The read-check-write therefore runs under a Web Lock, which
   * makes it exclusive across tabs and `isClaimed` authoritative inside it.
   */
  claim(notificationId: number, now: number = Date.now()): Promise<boolean> {
    const locks = this.lockManager();
    if (!locks) {
      return Promise.resolve(this.takeClaim(notificationId, now));
    }
    return locks
      .request(DESKTOP_CLAIM_LOCK, () => this.takeClaim(notificationId, now))
      .catch(() => this.takeClaim(notificationId, now));
  }

  admit(entry: BatchEntry, now: number = Date.now()): BatchEmission | null {
    const decision = admit(this.read(), now, entry);
    this.write(decision.state);
    return decision.emission;
  }

  flush(now: number = Date.now()): BatchEmission | null {
    const decision = flush(this.read(), now);
    this.write(decision.state);
    return decision.emission;
  }

  /**
   * Whether a flush would have anything to emit. Read-only on purpose: `flush()` drains
   * the shared queue as a side effect, so a tab that cannot render the result has to be
   * able to ask without consuming it.
   */
  hasPending(): boolean {
    return this.read().pending.length > 0;
  }

  // ── Claim ledger ───────────────────────────────────────────────────────────

  private takeClaim(notificationId: number, now: number): boolean {
    const state = this.read();
    if (isClaimed(state, notificationId)) {
      return false;
    }
    this.write(recordClaim(state, now, notificationId, this.tabId));
    return true;
  }

  /**
   * Absent before Firefox 96 and Safari 15.4, where the claim stays best-effort and a
   * duplicate banner remains possible.
   */
  private lockManager(): LockManager | null {
    return (navigator as Navigator & { locks?: LockManager }).locks ?? null;
  }

  // ── Storage ────────────────────────────────────────────────────────────────

  private read(): BatchState {
    try {
      const raw = localStorage.getItem(DESKTOP_NOTIFICATION_BATCH_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === 'object' && Array.isArray(parsed.pending)) {
        return { ...(parsed as BatchState), seen: Array.isArray(parsed.seen) ? parsed.seen : [] };
      }
    } catch {
      // fall through to empty state on malformed JSON / disabled storage
    }
    return EMPTY_BATCH_STATE;
  }

  private write(state: BatchState): void {
    try {
      localStorage.setItem(DESKTOP_NOTIFICATION_BATCH_KEY, JSON.stringify(state));
    } catch {
      // ignore quota / disabled storage errors — the window is best-effort
    }
  }
}
